import { useState, useRef, useEffect } from 'react';

type Props = {
  logicCount: number;
  onShowLogic: () => void;
  onShowAdvanced: () => void;
  onInsertBelow: () => void;
  onRemove: () => void;
  /** 0901 新增：下方插入分页栏 */
  onInsertPagebreakBelow?: () => void;
  /** 0901 新增：下方插入标题说明 */
  onInsertStatementBelow?: () => void;
  /** 0904 新增：进入批量删除模式（多选题目后批量删） */
  onBatchDelete?: () => void;
  /** 0909 新增：打开批量移动弹窗（按区间移到指定目标的前/后面） */
  onAskBatchMove?: () => void;
  /** 0904 新增：跳题逻辑（弹窗） */
  onShowJump?: () => void;
  /** 0904 新增：本题已配置的跳题条目数（按选项 + 无条件各算 1；用于菜单 badge） */
  jumpCount?: number;
  /** 1627 新增：题目关联（弹窗） */
  onShowCorrelation?: () => void;
  /** 1627 新增：本题已配置的关联规则数（数组长度；用于菜单 badge） */
  correlationCount?: number;
  /** 0907 新增：选项关联（本题选项随源题选项联动展示；仅选择题有） */
  onShowOptionLinks?: () => void;
  /** 0907 新增：本题已配置的选项关联绑定数（菜单 badge） */
  optionLinkCount?: number;
};

export default function QuestionMenu({
  logicCount,
  onShowLogic,
  onShowAdvanced,
  onInsertBelow,
  onRemove,
  onInsertPagebreakBelow,
  onInsertStatementBelow,
  onBatchDelete,
  onAskBatchMove,
  onShowJump,
  jumpCount = 0,
  onShowCorrelation,
  correlationCount = 0,
  onShowOptionLinks,
  optionLinkCount = 0,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="q-menu" ref={rootRef}>
      <button
        type="button"
        className="q-menu-trigger"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        title="题目操作"
      >
        ⋮
      </button>
      {open && (
        <div className="q-menu-popup" role="menu">
          <button className="q-menu-item" onClick={() => { close(); onShowLogic(); }}>
            <span>题目显示逻辑</span>
            {logicCount > 0 && <span className="q-menu-count">{logicCount}</span>}
          </button>
          {onShowJump && (
            <button className="q-menu-item" onClick={() => { close(); onShowJump(); }} title="答完此题后跳到指定题">
              <span>跳题逻辑</span>
              {jumpCount > 0 && <span className="q-menu-count">{jumpCount}</span>}
            </button>
          )}
          {/* 0907 选项关联：紧跟「跳题逻辑」下方；源题选某选项时本题只展示绑定的子选项 */}
          {onShowOptionLinks && (
            <button className="q-menu-item" onClick={() => { close(); onShowOptionLinks(); }} title="源题选中某选项时，本题只展示绑定的那部分选项；无绑定命中时本题隐藏">
              <span>选项关联</span>
              {optionLinkCount > 0 && <span className="q-menu-count">{optionLinkCount}</span>}
            </button>
          )}
          {onShowCorrelation && (
            <button className="q-menu-item" onClick={() => { close(); onShowCorrelation(); }} title="选中前置题的指定选项时，才显示当前题">
              <span>题目关联</span>
              {correlationCount > 0 && <span className="q-menu-count">{correlationCount}</span>}
            </button>
          )}
          <button className="q-menu-item" onClick={() => { close(); onInsertBelow(); }}>
            <span>下方插入新题</span>
          </button>
          {onInsertStatementBelow && (
            <button className="q-menu-item" onClick={() => { close(); onInsertStatementBelow(); }} title="在下方插入一个标题说明（不可作答）">
              <span>下方插入标题说明</span>
            </button>
          )}
          {onInsertPagebreakBelow && (
            <button className="q-menu-item" onClick={() => { close(); onInsertPagebreakBelow(); }} title="在下方插入分页栏，填写端将按页切换">
              <span>下方插入分页栏</span>
            </button>
          )}
          <div className="q-menu-divider" />
          <button className="q-menu-item" onClick={() => { close(); onShowAdvanced(); }}>
            <span>高级设置</span>
          </button>
          <div className="q-menu-divider" />
          {onAskBatchMove && (
            <button className="q-menu-item" onClick={() => { close(); onAskBatchMove(); }} title="选一段连续题目，整体移动到指定目标题目的前面或后面">
              <span>批量移动</span>
            </button>
          )}
          <button className="q-menu-item q-menu-item-danger" onClick={() => { close(); onRemove(); }}>
            <span>删除</span>
          </button>
          {/* 0904 批量删除：进入多选模式，勾选后一键删。放在「删除」下方，避免与单击删除混淆 */}
          {onBatchDelete && (
            <button className="q-menu-item q-menu-item-danger" onClick={() => { close(); onBatchDelete(); }} title="进入批量删除模式：勾选多道题目后一次性删除">
              <span>批量删除</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}